import { normalizeAlgebra } from "@/lib/tool-kit/algebra/parse";
import {
  type RelationSolution,
  solveRelation,
} from "@/lib/tool-kit/algebra/relation";
import {
  countOf,
  parseSides,
  type StatementError,
  singleVariable,
} from "@/lib/tool-kit/algebra/statement";
import { type Uni, uniEval } from "@/lib/tool-kit/algebra/uni";
import type { Rational } from "@/lib/tool-kit/math/rational";

export type LinearReading =
  | { ok: false; error: StatementError }
  | {
      ok: true;
      variable: string;
      left: Uni;
      right: Uni;
      solution: RelationSolution;
    };

/** Read and solve one linear equation such as `3(x − 2) + 4 = 2x + 7`. */
export function solveLinearText(input: string): LinearReading {
  const text = normalizeAlgebra(input);
  const equals = countOf(text, "=");
  if (equals === 0) {
    return { ok: false, error: { code: "no-relation", token: "" } };
  }
  if (equals > 1) {
    return { ok: false, error: { code: "many-relations", token: "=" } };
  }
  const sides = parseSides(text.split("="));
  if (!sides.ok) {
    return { ok: false, error: sides.error };
  }
  const read = singleVariable(sides.polys);
  if (!read.ok) {
    return { ok: false, error: read.error };
  }
  const [left, right] = read.parts;
  return {
    ok: true,
    variable: read.variable,
    left,
    right,
    solution: solveRelation([left, right], ["="]),
  };
}

/** Both sides of the original equation at a value — the check step. */
export function checkAt(
  left: Uni,
  right: Uni,
  value: Rational
): { left: Rational; right: Rational } {
  return { left: uniEval(left, value), right: uniEval(right, value) };
}
